import { FormEvent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { formatAge, formatCount } from '../../api/queueMonitor';
import { ReprocessItemResult } from '../../api/replay';
import {
    ROUTE_LABEL,
    STOP_LABEL,
    TraceAction,
    TraceInbound,
    TraceRelated,
    TraceRouteReport,
    TraceSearch,
    TraceSubject,
    VIA_HINT,
    VIA_LABEL,
} from '../../api/trace';
import { TraceMonitor } from '../../hooks/useTrace';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import { TraceCallout } from './TraceCallout';
import { TraceChip } from './TraceChip';
import { TraceFlow, stationAnchorId } from './TraceFlow';
import { TraceProgressPanel } from './TraceProgressPanel';
import './Trace.scss';

export interface ITracePanelProps {
    monitor: TraceMonitor;

    /**
     * Offers this object again, on the queue the trace worked out it belongs on.
     *
     * The queue travels with the call rather than being read off the dropdown above the
     * error log. A message on the wrong transfer queue is deleted by a handler that has no
     * case for its type, after which the same function writes the object into the sync log
     * as successfully synchronised - so choosing wrong is not a failure anybody can spot
     * afterwards.
     */
    onRequeue: (subject: TraceSubject, queue?: string | null) => void;

    /** Whether a requeue is running, so the button cannot be pressed twice. */
    requeueBusy: boolean;

    /**
     * Offers the raw webhook body of this object to the receive path again.
     *
     * The other way back, for the other half of the chain. A requeue needs a stored copy of
     * the connector's own model; this needs only the body the other system sent, which is
     * what is left when the receive function refused the event and swallowed the refusal.
     */
    onReprocess: (subject: TraceSubject) => void;

    /** Whether that call is running. */
    reprocessBusy: boolean;

    /**
     * Fetches this object out of Chargebee again and hands it in as the missing webhook.
     *
     * The third way back, and the only one that does not depend on anything the connector
     * kept. What comes back is the object as Chargebee has it now, which is a reason to
     * prefer it and a reason to be careful with it.
     */
    onReplay: (subject: TraceSubject) => void;

    /** Whether that fetch is running. */
    replayBusy: boolean;

    /**
     * Opens the queue card that holds the poison messages of one queue.
     *
     * The fourth way back is the one this panel points at rather than performs: moving a
     * poison message happens next to the messages themselves, where you can see which ones
     * are being moved. So the button here navigates - the same thing the health panel does
     * with the same action - instead of sitting there disabled, which is what it did while
     * the panel had no handler for it at all.
     */
    onOpenPoison: (queue: string) => void;

    /**
     * Opens the dossier of this object: the invoice it belongs to with everything around it.
     * Left out, no button is offered.
     */
    onOpenDossier?: (id: string) => void;

    /** What became of the last one, or null when none has run. */
    reprocessResult: ReprocessItemResult | null;

    /** What went wrong with it, when anything did. */
    reprocessError: string | null;

    /** Clears that result, so the panel is not stuck showing an old one. */
    onDismissReprocess: () => void;

    open: boolean;
    onToggle: () => void;
}

/** What the search box takes, said once so nobody has to guess. */
const SEARCH_HINT =
    'Plak hier een factuurnummer, klantnummer, transactie-id, ObjectGuid of Exact-id. Er wordt alleen gelezen: er verandert niets in de queue, in Chargebee of in Exact.';

/** Where the days came from, per source, since the three claims are not equally strong. */
const WINDOW_TITLE: Record<string, string> = {
    SyncLog:
        'De dagen die zijn gelezen. Die komen uit de synclog, die per dag bijhoudt of dit object is verwerkt - daarom hoeft er geen twee weken aan logboeken doorzocht te worden.',
    RequestedRange:
        'De synclog kent dit object niet, dus deze dagen zijn niet vastgesteld maar overgenomen uit de gekozen periode.',
    Evidence:
        'De synclog kent dit object niet, maar er is wel iets van gevonden. Rond die dag zijn de logboeken gelezen, plus de nieuwste dagen.',
};

/** The one word after the range that says how hard it is. */
const WINDOW_SUFFIX: Record<string, string> = {
    SyncLog: '',
    RequestedRange: ' (geraden)',
    Evidence: ' (rond de vondst)',
};

const BASIS_HINT: Record<string, string> = {
    Topology:
        'Afgeleid uit de code: dit objecttype gaat via deze queue, en deze function luistert daarop. Het is geen waarneming dat juist dit bericht daar langs is gekomen.',
    Observation: 'Het bericht is zojuist in deze queue gezien.',
};

/** The icon of a verdict, so the conclusion reads before it is read. */
const TONE_ICON: Record<string, IconProp> = {
    ok: 'check',
    warn: 'clock',
    error: 'triangle-exclamation',
    unknown: 'circle-question',
};

/** A day, without the time that would only be noise. */
const dayLabel = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short',
          });
};

/** A day with its year, for the sentences about how far back a search got. */
const fullDayLabel = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleDateString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          });
};

/** A moment with its time, for when the send function was done with an object. */
const formatDateTime = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          });
};

/** How the send function was done with a payload, in the words the server uses for it. */
const payloadOutcomeText = (outcome?: string | null): string => {
    switch (outcome) {
        case 'Succeeded':
            return 'gelukt';
        case 'AlreadySynced':
            return 'stond er al';
        case 'Unhandled':
            return 'niets te doen voor de connector';
        default:
            return 'afgehandeld';
    }
};

/** How many days a window spans end to end, gaps included. */
const dayCount = (window: { from: string; to: string }): number => {
    const first = Date.parse(window.from);
    const last = Date.parse(window.to);

    return Number.isNaN(first) || Number.isNaN(last)
        ? 0
        : Math.round((last - first) / 86400000) + 1;
};

/**
 * What one route did, in one line.
 *
 * Three states rather than two, and the third is the one that was missing. A route that was
 * skipped is not a route that found nothing: "niet gezocht, want dit werkt alleen met een
 * ObjectGuid" is actionable and "niets gevonden" is not.
 */
const RouteRow = (props: { route: TraceRouteReport }) => {
    const { route } = props;
    const known = ROUTE_LABEL[route.route];

    const icon: IconProp = !route.searched
        ? 'circle-question'
        : route.hits > 0
        ? 'check'
        : 'xmark';

    const state = !route.searched ? 'skipped' : route.hits > 0 ? 'hit' : 'miss';

    return (
        <li className={`trace-search__route trace-search__route--${state}`}>
            <FontAwesomeIcon icon={icon} className="trace-search__icon" />

            <span className="trace-search__route-label" title={known?.where}>
                {known?.label ?? route.route}
            </span>

            <span className="trace-search__route-text">
                {route.explanation}
            </span>
        </li>
    );
};

/**
 * The raw webhooks that name this id, and which of them is actually about it.
 *
 * The one list nobody could get at before. These bodies are keyed by Chargebee's event id,
 * so no key route reaches them and no filter in a storage browser searches inside them -
 * which left "acht rijen die mijn factuurnummer noemen" as something you could see but not
 * interpret.
 *
 * The split down the middle is the whole point. A payment webhook carries the invoice it
 * paid, so an invoice number matches on every payment against it, and only a body whose own
 * object is the one you searched for is worth offering to the receive path again.
 */
const InboundReport = (props: { inbound: TraceInbound }) => {
    const { inbound } = props;

    if (inbound.hits.length === 0 && inbound.unread.length === 0) {
        return null;
    }

    const subjects = inbound.hits.filter((hit) => hit.isSubject);
    const context = inbound.hits.filter((hit) => !hit.isSubject);

    return (
        <details className="trace-inbound">
            <summary className="trace-inbound__summary">
                Ontvangen webhooks die dit id noemen
                <TraceChip
                    tone={subjects.length > 0 ? 'ok' : 'muted'}
                    icon="check"
                    label="van dit object"
                    hint="Berichten waarvan dit object zelf het onderwerp is. Alleen die zijn het waard om opnieuw aan te bieden."
                >
                    {subjects.length}
                </TraceChip>
                <TraceChip
                    tone="muted"
                    icon="circle-info"
                    label="van iets anders"
                    hint="Berichten die dit id wel noemen maar over een ander object gaan - meestal betalingen op deze factuur."
                >
                    {context.length}
                </TraceChip>
                <span className="trace-inbound__window">
                    {inbound.scanned} onverwerkt bericht
                    {inbound.scanned === 1 ? '' : 'en'} gelezen
                </span>
            </summary>

            <TraceCallout tone="info" title={null}>
                Dit zijn de ruwe berichten die nog niet verwerkt zijn. Wat wél
                verwerkt is, staat hier niet meer in: de ontvangstfunction
                verwijdert een bericht zodra het gelukt is.
            </TraceCallout>

            <ul className="trace-inbound__hits">
                {inbound.hits.map((hit) => (
                    <li
                        key={`${hit.table}|${hit.eventType}|${hit.eventId}`}
                        className={`trace-inbound__hit trace-inbound__hit--${
                            hit.isSubject ? 'subject' : 'context'
                        }`}
                    >
                        <FontAwesomeIcon
                            icon={hit.isSubject ? 'check' : 'circle-info'}
                            className="trace-inbound__icon"
                        />

                        <code className="trace-inbound__event">
                            {hit.eventType}
                        </code>

                        <span className="trace-inbound__why">
                            {hit.isSubject
                                ? 'is de webhook van dit object'
                                : hit.matchedAs
                                ? `noemt dit id als ${hit.matchedAs}, maar gaat over iets anders`
                                : 'noemt dit id ergens in de tekst'}
                        </span>

                        <code
                            className="trace-inbound__key"
                            title="Gebeurtenis-id: de rijsleutel waaronder dit bericht bewaard is."
                        >
                            {hit.eventId}
                        </code>
                    </li>
                ))}
            </ul>

            {inbound.unread.length > 0 && (
                <TraceCallout tone="warn" title="Niet volledig gelezen">
                    Deze tabellen konden niet gelezen worden:{' '}
                    {inbound.unread.join(', ')}. Dat ze niets opleverden bewijst
                    hier dus niets.
                </TraceCallout>
            )}

            {inbound.truncated && (
                <TraceCallout tone="warn" title="Afgekapt">
                    De zoekactie is op zijn grens gestopt, dus dit is geen
                    volledig antwoord.
                </TraceCallout>
            )}
        </details>
    );
};

/**
 * Which routes ran, how far back they went, and the button for the days before that.
 *
 * Folded away by default now that the journey carries the answer. It used to open itself
 * whenever nothing was found, which was right when this was the only thing on screen that
 * could explain an absence - and wrong the moment there is a chain above it that says the
 * same thing in one line.
 */
const SearchReport = (props: {
    search: TraceSearch;
    /** The report of the last round, which names the days a next attempt would read. */
    next?: TraceSearch;
    /** The rounds of walking backwards that turned up nothing new. */
    rounds: TraceSearch[];
    loading: boolean;
    onSearchFurtherBack: () => void;
}) => {
    const { search } = props;

    const window =
        search.daysSearched > 0
            ? `${search.daysSearched} ${
                  search.daysSearched === 1 ? 'dag' : 'dagen'
              } logboek gelezen, ${fullDayLabel(
                  search.from
              )} t/m ${fullDayLabel(search.to)}`
            : 'geen logboekdagen gelezen';

    const hits = search.routes.filter((route) => route.hits > 0).length;
    const misses = search.routes.filter(
        (route) => route.searched && route.hits === 0
    ).length;
    const skipped = search.routes.filter((route) => !route.searched).length;

    return (
        <details className="trace-search">
            <summary className="trace-search__summary">
                Waar is er gezocht?
                <TraceChip
                    tone={hits > 0 ? 'ok' : 'muted'}
                    icon="check"
                    label="treffer"
                    hint="Zoekroutes die iets over dit id opleverden."
                >
                    {hits}
                </TraceChip>
                <TraceChip
                    tone="muted"
                    icon="xmark"
                    label="leeg"
                    hint="Zoekroutes die zijn gelopen en niets vonden. Wat dat betekent, staat per route erbij."
                >
                    {misses}
                </TraceChip>
                <TraceChip
                    tone="muted"
                    icon="circle-question"
                    label="niet gelopen"
                    hint="Zoekroutes waarvoor de sleutel ontbrak. Die zeggen dus niets."
                >
                    {skipped}
                </TraceChip>
                <span className="trace-search__window">{window}</span>
            </summary>

            <ul className="trace-search__routes">
                {search.routes.map((route) => (
                    <RouteRow key={route.route} route={route} />
                ))}
            </ul>

            <TraceCallout tone="info" title="Waarom hier gestopt">
                {STOP_LABEL[search.stoppedBecause]}
            </TraceCallout>

            {props.rounds.length > 0 && (
                <p className="trace-search__rounds">
                    Daarna is er nog verder terug gezocht, zonder dat er meer
                    over dit object bleek:{' '}
                    {props.rounds
                        .map(
                            (round) =>
                                `${fullDayLabel(round.from)} t/m ${fullDayLabel(
                                    round.to
                                )} (${round.daysSearched} dagen)`
                        )
                        .join(', ')}
                    .
                </p>
            )}

            {props.next?.moreDaysAvailable && (
                <p className="trace-search__more">
                    <SearchFurtherBack
                        search={props.next}
                        loading={props.loading}
                        onSearchFurtherBack={props.onSearchFurtherBack}
                    />
                </p>
            )}
        </details>
    );
};

/**
 * The button for the days before the ones just read.
 *
 * Rendered next to the answer rather than only inside the search report, which is folded
 * away by default. The report explains how the search went and that is worth one click; the
 * button is what somebody staring at "hier staat niets over dit object" actually needs, and
 * an action nobody can find is an action that does not exist.
 */
const SearchFurtherBack = (props: {
    search: TraceSearch;
    loading: boolean;
    onSearchFurtherBack: () => void;
}) =>
    props.search.moreDaysAvailable ? (
        <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={props.onSearchFurtherBack}
            disabled={props.loading}
            title="Leest de dagpartities vóór de dagen die net zijn doorzocht. Wat er nu op het scherm staat blijft staan: levert deze periode niets nieuws op, dan wordt hij alleen bij de doorzochte periodes gezet. Dit blijft te herhalen - de logboeken worden niet opgeruimd, dus er is geen datum waarop ze aantoonbaar ophouden."
        >
            {props.loading ? (
                <FontAwesomeIcon icon="spinner" spin />
            ) : (
                <FontAwesomeIcon icon="magnifying-glass" />
            )}
            <span className="ms-2">
                Zoek verder terug: {fullDayLabel(props.search.nextFrom)} t/m{' '}
                {fullDayLabel(props.search.nextTo)}
            </span>
        </button>
    ) : null;

/**
 * One thing that can be done from here, with why it would be premature under it.
 *
 * Two acts behind one shape, and which one a button is decides what it needs. Offering an
 * object again writes a message naming the stored copy of the connector's model, so it needs an
 * ObjectGuid; offering a raw webhook again writes a message naming the body the other system
 * sent, so it needs the key that body is stored under. An object stuck in the first half of the
 * chain has the second and not the first - which is why the panel used to end in a button that
 * could not be pressed, above a warning that the thing it needed did not exist.
 */
export interface ITraceActions {
    subject: TraceSubject;
    actions: TraceAction[];
    requeueBusy: boolean;
    reprocessBusy: boolean;
    replayBusy: boolean;
    onRequeue: (subject: TraceSubject, queue?: string | null) => void;
    onReprocess: (subject: TraceSubject) => void;
    onReplay: (subject: TraceSubject) => void;
    onOpenPoison: (queue: string) => void;
}

/**
 * The icon of each way back.
 *
 * Three buttons that look identical are three buttons a reader has to read twice. These are
 * different acts on different data - a body that is already here, a message rebuilt from
 * keys, a fresh fetch out of Chargebee - and the icon is the first thing that says so.
 */
const ACTION_ICON: Record<string, IconProp> = {
    ReprocessInbound: 'inbox',
    ReplayFromSource: 'cloud-arrow-down',
    OfferAgainNow: 'rotate',
    RequeuePoison: 'rotate-left',
};

const ActionButton = (props: {
    action: TraceAction;
    actions: ITraceActions;
    /** Compact leaves out what to expect, for the copy that sits inside a step. */
    compact?: boolean;
}) => {
    const { action } = props;
    const { subject } = props.actions;

    /*
        The one way back this panel points at rather than performs.

        Moving a poison message happens in the queue card, next to the messages it would move,
        so that whoever presses it can see which ones those are. This button therefore
        navigates - the same thing the health panel does with the same action - which is an
        improvement on what it did before, namely sit there permanently disabled with nothing
        saying why.
    */
    const navigates = action.kind === 'RequeuePoison';

    const ready =
        action.kind === 'ReprocessInbound'
            ? !!subject.inboundEventType && !!subject.inboundEventId
            : action.kind === 'ReplayFromSource'
            ? !!subject.relationId
            : action.kind === 'OfferAgainNow'
            ? !!subject.objectGuid && !!subject.relationId && !!action.queue
            : navigates
            ? !!action.queue
            : false;

    const busy =
        action.kind === 'ReprocessInbound'
            ? props.actions.reprocessBusy
            : action.kind === 'ReplayFromSource'
            ? props.actions.replayBusy
            : navigates
            ? false
            : props.actions.requeueBusy;

    const run = () => {
        if (action.kind === 'ReprocessInbound') {
            props.actions.onReprocess(subject);
        } else if (action.kind === 'ReplayFromSource') {
            props.actions.onReplay(subject);
        } else if (navigates && action.queue) {
            props.actions.onOpenPoison(action.queue);
        } else {
            props.actions.onRequeue(subject, action.queue);
        }
    };

    return (
        <div
            className={[
                'trace-action',
                action.recommended ? 'trace-action--recommended' : '',
                action.blocked ? 'trace-action--blocked' : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {/*
                A banner across the top rather than a word stuck behind the button.

                "Doe dit" was two words in a green pill next to a blue button, which said
                that something about this one was different without saying what, and left the
                reader to guess whether the pill was a label, a state or a promise. This says
                which of the ways back follows from the diagnosis, and the button under it is
                the only green thing on the screen.
            */}
            {action.recommended && (
                <span className="trace-action__banner">
                    <FontAwesomeIcon icon="lightbulb" />
                    Aanbevolen: dit volgt uit de diagnose hierboven
                </span>
            )}

            {action.blocked && (
                <span className="trace-action__banner trace-action__banner--blocked">
                    <FontAwesomeIcon icon="ban" />
                    Kan niet: deze weg is voor dit object afgesloten
                </span>
            )}

            <div className="trace-action__top">
                <button
                    type="button"
                    className={
                        action.recommended
                            ? 'trace-action__button trace-action__button--go'
                            : 'trace-action__button'
                    }
                    disabled={busy || !ready || action.blocked}
                    title={action.description}
                    onClick={run}
                >
                    <FontAwesomeIcon
                        icon={
                            busy
                                ? 'spinner'
                                : ACTION_ICON[action.kind] ?? 'bolt'
                        }
                        spin={busy}
                    />
                    <span className="trace-action__button-label">
                        {action.label}
                    </span>
                </button>

                {/*
                    The destination on the button rather than in the paragraph under it. There
                    is no dropdown to pick a queue any more - the wrong one deletes the message
                    and records a success - so the only honest place for that choice is the
                    server, and this is it saying which one it made.
                */}
                {action.queue && (
                    <TraceChip
                        label="bericht komt op"
                        tone="info"
                        mono
                        hint="Deze queue heeft de server gekozen op basis van het objecttype. Een bericht op de verkeerde queue wordt weggegooid en als geslaagd geboekt, dus dit valt hier niet te kiezen."
                    >
                        {action.queue}
                    </TraceChip>
                )}
            </div>

            <dl className="trace-action__facts">
                <div className="trace-action__fact">
                    <dt>Wat gebeurt er</dt>
                    <dd>
                        {action.description}
                        {navigates &&
                            ' Deze knop brengt je naar de queuekaart van die poison queue: daar staan de berichten zelf, en zet je ze met de knop daar terug.'}
                    </dd>
                </div>

                {/*
                    And how you tell whether it worked, which is the half that was missing.
                    Every one of these buttons has the same two possible outcomes - a koppeling
                    or a fresh line in the log - and without that written down the next click
                    after pressing is a page refresh in the hope of a different answer.
                */}
                {!props.compact && action.expectation && (
                    <div className="trace-action__fact">
                        <dt>Wat kun je verwachten</dt>
                        <dd>{action.expectation}</dd>
                    </div>
                )}
            </dl>

            {!ready && !action.blocked && !action.caveat && (
                <TraceCallout tone="muted" title="Nog niet mogelijk">
                    De sleutel die hiervoor nodig is, is in deze omgeving niet
                    gevonden. Wat er wél kan, staat bij de andere knoppen.
                </TraceCallout>
            )}

            {action.caveat && (
                <TraceCallout
                    tone={action.blocked ? 'muted' : 'warn'}
                    title={action.blocked ? 'Waarom niet' : 'Let op'}
                    icon={action.blocked ? 'ban' : undefined}
                >
                    {action.caveat}
                </TraceCallout>
            )}
        </div>
    );
};

/**
 * The ways back, side by side, with the one that follows from the verdict marked.
 *
 * Side by side rather than one at a time, and that is the whole point of the block. The three
 * are different acts on different data - the raw body, the stored copy, the source system - and
 * the question a reader has in front of a stuck object is not "is there a button" but "which of
 * these, and why not the others". A screen that shows one button answers the wrong question,
 * and one that shows three without a recommendation answers none.
 */
const ActionBlock = (props: { actions: ITraceActions }) => {
    const { actions } = props.actions;

    if (actions.length === 0) {
        return null;
    }

    return (
        <div className="trace-actions">
            <div className="trace-actions__head">
                <span className="trace-actions__title">
                    Wat kun je hiermee doen?
                </span>

                <span className="trace-actions__hint">
                    Elke knop werkt op andere data. Er staat bij wat er precies
                    gebeurt en waaraan je daarna ziet of het gelukt is.
                </span>
            </div>

            {actions.map((action) => (
                <ActionButton
                    key={action.kind}
                    action={action}
                    actions={props.actions}
                />
            ))}
        </div>
    );
};

/** What the queue tone of a reprocess outcome is, so the report reads before it is read. */
const REPROCESS_TONE: Record<string, 'ok' | 'warn' | 'error'> = {
    Queued: 'ok',
    WouldQueue: 'ok',
    NotStored: 'warn',
    Failed: 'error',
};

/**
 * What became of offering a stored webhook again.
 *
 * Stays until it is dismissed, with a reload next to it. The receive path runs within seconds
 * and the trace is not polling, so without that button the answer to "did it work" would be a
 * page refresh.
 */
const ReprocessReport = (props: {
    result: ReprocessItemResult | null;
    error: string | null;
    onDismiss: () => void;
    onReload: () => void;
}) => {
    const tone = props.error
        ? 'error'
        : REPROCESS_TONE[props.result?.outcome ?? 'Failed'] ?? 'warn';

    return (
        <div className="trace-reprocess">
            <TraceCallout
                tone={
                    tone === 'ok' ? 'ok' : tone === 'error' ? 'danger' : 'warn'
                }
                title={
                    tone === 'ok'
                        ? 'Klaargezet'
                        : tone === 'error'
                        ? 'Niet gelukt'
                        : 'Er is niets weggezet'
                }
                actions={
                    <>
                        <button
                            type="button"
                            className="requeue-link"
                            onClick={props.onReload}
                        >
                            Nu opnieuw volgen
                        </button>

                        <button
                            type="button"
                            className="requeue-link requeue-link--muted"
                            onClick={props.onDismiss}
                        >
                            Sluiten
                        </button>
                    </>
                }
            >
                {props.error ?? props.result?.summary}

                {props.result?.message && (
                    <span className="trace-reprocess__message">
                        Bericht <code>{props.result.message}</code>
                        {props.result.queueName && (
                            <>
                                {' '}
                                op queue <code>{props.result.queueName}</code>
                            </>
                        )}
                        .
                    </span>
                )}

                {tone === 'ok' && (
                    <span className="trace-reprocess__message">
                        De function pakt dat binnen enkele seconden op. Volg dit
                        object daarna opnieuw: dan staat hierboven of het deze
                        keer verder is gekomen.
                    </span>
                )}
            </TraceCallout>
        </div>
    );
};

/**
 * In Exact or not, in one badge, with the id in it when there is one.
 *
 * The id belongs on the badge and not only in its tooltip. "In Exact" is the answer to the
 * question, and the id is the thing somebody does next with that answer - paste it into
 * Exact and look - so hiding it behind a hover made the badge a dead end.
 */
const ExactBadge = (props: { inExact: boolean; dependingId?: string | null }) =>
    props.inExact ? (
        <TraceChip
            tone="ok"
            icon="circle-check"
            label="in Exact"
            mono
            hint="Er staat een Exact-id bij dit object, en dat is het enige waaraan te zien is dat het daar is aangekomen."
        >
            {props.dependingId}
        </TraceChip>
    ) : (
        <TraceChip
            tone="error"
            icon="circle-exclamation"
            hint="Er staat geen Exact-id bij dit object, dus het is daar niet aangekomen."
        >
            niet in Exact
        </TraceChip>
    );

/** One related object, with the thing you came for: staat die factuur er inmiddels. */
const RelatedRow = (props: { related: TraceRelated; onOpen: () => void }) => (
    <li className="trace-related__row">
        <span className="trace-related__type">{props.related.objectType}</span>

        <button
            type="button"
            className="trace-related__id"
            onClick={props.onOpen}
            title="Volg dit object"
        >
            {props.related.relationId}
        </button>

        <TraceChip
            label="verband"
            tone="neutral"
            icon="link"
            hint={VIA_HINT[props.related.via]}
        >
            {VIA_LABEL[props.related.via]}
        </TraceChip>

        <ExactBadge
            inExact={props.related.inExact}
            dependingId={props.related.dependingId}
        />

        {props.related.attempts > 0 && (
            <TraceChip
                label="regels"
                tone="neutral"
                hint="Zoveel regels over dit object zijn er in de gelezen dagen gevonden."
            >
                {formatCount(props.related.attempts)}
            </TraceChip>
        )}

        <span
            className="trace-related__message"
            title={props.related.lastMessage ?? ''}
        >
            {props.related.lastMessage}
        </span>
    </li>
);

/**
 * Where one object stands, what happened to it, and what is waiting on it.
 *
 * Reads top to bottom as the question is asked. The verdict answers it in one sentence and
 * says what to do next; the chain shows where on its road the object is standing and what
 * every station holds about it; the timeline is the log behind that, filtered to whichever
 * station you clicked. Everything that explains how the trace itself went - which routes
 * ran, which days were read, what the tables cannot show - is folded away underneath,
 * because that is the answer to a different question and it used to be the first thing on
 * screen.
 */
export const TracePanel = (props: ITracePanelProps) => {
    const { monitor } = props;
    const { trace } = monitor;

    const [term, setTerm] = useState<string>('');

    // The box follows what is on screen, so a trace opened from a row in the table leaves
    // its id in the box rather than whatever was typed there before.
    useEffect(() => {
        setTerm(monitor.request?.id ?? monitor.request?.relationId ?? '');
    }, [monitor.request]);

    const submit = (event: FormEvent) => {
        event.preventDefault();

        const trimmed = term.trim();

        if (trimmed) {
            monitor.open({ id: trimmed });
        }
    };

    const subject = trace?.subject ?? null;

    // The dossier works out for itself what an id belongs to, so the object's own id will do.
    const dossierId = subject?.relationId ?? null;
    const journey = trace?.journey ?? null;
    const dayWindow = trace?.query.window ?? null;
    const guessed = dayWindow?.source === 'RequestedRange';

    /*
        The report of the last round, which is not always the one on the trace.

        After a round of walking backwards that found nothing, the trace on screen is the older
        find and the days left to read are the ones before that empty round - so the button has
        to take its chunk from the round rather than from the answer.
    */
    const lastSearch =
        monitor.alsoSearched.length > 0
            ? monitor.alsoSearched[monitor.alsoSearched.length - 1]
            : trace?.search;

    /** Whether the trace has an answer, as opposed to having nothing to report yet. */
    const settled = !!journey && journey.tone !== 'unknown';

    /*
        The step the verdict is about, for the link that jumps to it.

        The chain is ten steps long and the one that matters is rarely the first, so the
        sentence at the top and the evidence for it were a scroll apart with eight cards in
        between. Failure first: where an object broke beats where it got to, and on the step
        that is both, they are the same station anyway.
    */
    const jumpTo = journey?.failedKey ?? journey?.currentKey ?? null;

    /*
        One bundle, handed to both places a button is drawn.

        The buttons appear twice on purpose - once with the verdict, once at the step they act
        on - and they have to be the same buttons with the same reasons behind them. Passing the
        bundle rather than the individual callbacks is what keeps a change in one from being a
        second button that behaves slightly differently in the other.
    */
    const actionProps: ITraceActions | null = subject
        ? {
              subject,
              actions: trace?.actions ?? [],
              requeueBusy: props.requeueBusy,
              reprocessBusy: props.reprocessBusy,
              replayBusy: props.replayBusy,
              onRequeue: props.onRequeue,
              onReprocess: props.onReprocess,
              onReplay: props.onReplay,
              onOpenPoison: props.onOpenPoison,
          }
        : null;

    return (
        <CollapsibleSection
            id="trace-panel"
            className="trace-panel"
            title="Object zoeken"
            hint={SEARCH_HINT}
            open={props.open}
            onToggle={props.onToggle}
            busy={monitor.loading}
            status={
                /*
                    Which object is on screen, so a folded section still says what is inside
                    it rather than only that something is.
                */
                subject ? (
                    <span className="trace-panel__subject">
                        {subject.objectType} {subject.relationId}
                    </span>
                ) : undefined
            }
        >
            {/*
                The search box lives in the body rather than in the header: a header that
                folds away has to keep to what can be read, and a form is something you do.
            */}
            <form className="trace-panel__search" onSubmit={submit}>
                <label className="visually-hidden" htmlFor="trace-search">
                    Id om te zoeken
                </label>

                <input
                    id="trace-search"
                    type="search"
                    className="form-control filter"
                    placeholder="Factuurnummer, klant-id, transactie-id, GUID"
                    value={term}
                    title={SEARCH_HINT}
                    onChange={(event) => setTerm(event.target.value)}
                />

                <button
                    type="submit"
                    className="btn btn-sm btn-outline-primary"
                    disabled={monitor.loading || !term.trim()}
                >
                    {monitor.loading ? (
                        <FontAwesomeIcon icon="spinner" spin />
                    ) : (
                        <FontAwesomeIcon icon="magnifying-glass" />
                    )}
                    <span className="ms-2">Zoeken</span>
                </button>

                {trace && (
                    <>
                        <button
                            type="button"
                            className="requeue-link"
                            onClick={monitor.reload}
                            disabled={monitor.loading}
                        >
                            Opnieuw ophalen
                        </button>

                        <button
                            type="button"
                            className="requeue-link requeue-link--muted"
                            onClick={monitor.close}
                        >
                            Sluiten
                        </button>
                    </>
                )}
            </form>

            {monitor.loading && (
                <TraceProgressPanel
                    progress={monitor.progress}
                    streaming={monitor.streaming}
                    onCancel={monitor.cancel}
                />
            )}

            {monitor.error && (
                <div role="alert">
                    <TraceCallout tone="danger" title="Volgen mislukt">
                        {monitor.error}
                    </TraceCallout>
                </div>
            )}

            {!trace && !monitor.loading && !monitor.error && (
                <TraceCallout tone="info" title="Nog niets gevolgd">
                    Plak hierboven een factuurnummer, klantnummer,
                    transactie-id, ObjectGuid of Exact-id - of klik op
                    &quot;Volgen&quot; bij een regel in het foutenlogboek. Er
                    wordt alleen gelezen: dit verandert niets in de queue, in
                    Chargebee of in Exact.
                </TraceCallout>
            )}

            {trace && trace.error && (
                <div role="alert">
                    <TraceCallout tone="danger" title="Niet volledig gelezen">
                        {trace.error}
                    </TraceCallout>
                </div>
            )}

            {trace && trace.candidates.length > 1 && (
                <div className="trace-candidates">
                    <span className="trace-candidates__label">
                        Dit id hoort bij meerdere objecten:
                    </span>

                    {trace.candidates.map((candidate) => (
                        <button
                            key={`${candidate.objectType}-${candidate.relationId}`}
                            type="button"
                            className={
                                subject?.objectType === candidate.objectType
                                    ? 'trace-candidates__pick trace-candidates__pick--current'
                                    : 'trace-candidates__pick'
                            }
                            onClick={() =>
                                monitor.open({
                                    id: trace.query.id ?? undefined,
                                    objectType: candidate.objectType,
                                    relationId:
                                        candidate.relationId ?? undefined,
                                })
                            }
                        >
                            {candidate.objectType}
                            {candidate.inExact && (
                                <FontAwesomeIcon
                                    icon="check"
                                    className="ms-2 text-success"
                                />
                            )}
                        </button>
                    ))}
                </div>
            )}

            {trace && !subject && (
                <TraceCallout
                    tone="warn"
                    title="Geen object gevonden"
                    actions={
                        lastSearch ? (
                            <SearchFurtherBack
                                search={lastSearch}
                                loading={monitor.loading}
                                onSearchFurtherBack={monitor.searchFurtherBack}
                            />
                        ) : undefined
                    }
                >
                    Er is geen object met id &quot;{trace.query.id}&quot; in
                    deze omgeving.
                    {trace.related.length > 0
                        ? ' Er zijn wel regels die dit id noemen; de objecten daaruit staan hieronder. Klik er een aan om te zien wat daarmee is gebeurd.'
                        : ' Onderaan staat per zoekroute wat er is gelezen en wat een leeg resultaat daar betekent.'}
                </TraceCallout>
            )}

            {trace && subject && (
                <>
                    {/*
                        The answer, before anything that supports it. Everything under this
                        block is evidence for the one sentence in it, and a reader who only
                        needs the sentence can stop here.
                    */}
                    <div
                        className={`trace-verdict trace-verdict--${
                            journey?.tone ?? 'unknown'
                        }`}
                    >
                        <div className="trace-verdict__head">
                            <FontAwesomeIcon
                                icon={TONE_ICON[journey?.tone ?? 'unknown']}
                                className="trace-verdict__icon"
                            />

                            <span className="trace-verdict__subject">
                                {subject.objectType}{' '}
                                <strong>{subject.relationId}</strong>
                            </span>

                            <ExactBadge
                                inExact={subject.inExact}
                                dependingId={subject.dependingId}
                            />

                            {subject.lastState && (
                                <TraceChip
                                    label="synclog"
                                    tone="neutral"
                                    icon="database"
                                    hint="De stand die de synclog aan het eind van de laatste bekende dag noteerde."
                                >
                                    {subject.lastState}
                                    {!!subject.failCount &&
                                        ` na ${formatCount(
                                            subject.failCount
                                        )} mislukte poging(en)`}
                                </TraceChip>
                            )}

                            {props.onOpenDossier && dossierId && (
                                <button
                                    type="button"
                                    className="requeue-link"
                                    onClick={() =>
                                        props.onOpenDossier?.(dossierId)
                                    }
                                    title="Dit object met alles eromheen - factuur, betalingen, storneringen, klant - op één tijdlijn"
                                >
                                    Dossier openen
                                </button>
                            )}

                            {subject.payloadExists === false && (
                                <TraceChip
                                    label="vertaald object"
                                    tone="warn"
                                    icon="triangle-exclamation"
                                    hint="Zonder de opgeslagen kopie kan de verzendfunction het bericht niet opnieuw opbouwen. Dat kan ook betekenen dat het versturen gelukt is en de kopie daarna is opgeruimd: de connector bewaart hem 30 dagen na verwerking."
                                >
                                    weg
                                </TraceChip>
                            )}

                            {subject.payloadExists === true &&
                                subject.payloadHandledAt && (
                                    <TraceChip
                                        label="vertaald object"
                                        tone="ok"
                                        icon="check"
                                        hint={`De verzendfunction was klaar met dit object op ${formatDateTime(subject.payloadHandledAt)} (${payloadOutcomeText(subject.payloadOutcome)}). De kopie blijft bewaard zodat je kunt zien wat er verstuurd is; opnieuw aanbieden doet niets meer.`}
                                    >
                                        verwerkt
                                    </TraceChip>
                                )}

                            {subject.waitingIn && (
                                <TraceChip
                                    label="wacht in"
                                    tone="warn"
                                    icon="layer-group"
                                    mono
                                    hint={`Op dit moment gezien op plek ${subject.waitingIn.position} van de voorste berichten. Verder in de keten staat het hele bericht.`}
                                >
                                    {subject.waitingIn.queue}
                                </TraceChip>
                            )}
                        </div>

                        <p className="trace-verdict__line">
                            {journey?.verdict}
                        </p>

                        {/*
                            The paragraph between the verdict and the evidence. Not folded away:
                            it is the difference between knowing where an object is stuck and
                            knowing what to do about it, and everything under it is only support.
                        */}
                        {journey?.explanation && (
                            <p className="trace-verdict__why">
                                {journey.explanation}
                            </p>
                        )}

                        {journey?.nextStep && (
                            <p className="trace-verdict__next">
                                <span className="trace-verdict__next-label">
                                    Volgende stap
                                </span>
                                {journey.nextStep}
                            </p>
                        )}

                        {jumpTo && (
                            <p className="trace-verdict__jump">
                                <button
                                    type="button"
                                    className="requeue-link"
                                    onClick={() =>
                                        document
                                            .getElementById(
                                                stationAnchorId(jumpTo)
                                            )
                                            ?.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center',
                                            })
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon="arrow-down"
                                        className="me-2"
                                    />
                                    {journey?.failedKey
                                        ? 'Naar de stap waar het misgaat'
                                        : 'Naar de stap waar dit object nu staat'}
                                </button>
                            </p>
                        )}

                        {/*
                            Right under the sentence that says to look further back, rather
                            than only inside the folded search report. This is the case where
                            the object was inferred from another object's column - a payment
                            naming an invoice - so the trace has a subject, an empty timeline
                            and nothing else to offer.

                            Only while the verdict is still open. Once the trace has an answer -
                            a body waiting, a message in a queue, a failed attempt - "zoek
                            verder terug" is not the next thing to do, and offering it as the
                            one button under the answer said it was.
                        */}
                        {!settled && lastSearch?.moreDaysAvailable && (
                            <p className="trace-verdict__search">
                                <SearchFurtherBack
                                    search={lastSearch}
                                    loading={monitor.loading}
                                    onSearchFurtherBack={
                                        monitor.searchFurtherBack
                                    }
                                />
                            </p>
                        )}

                        {monitor.alsoSearched.length > 0 && (
                            <p className="trace-verdict__also">
                                Ook doorzocht, zonder dat er meer over dit
                                object bleek:{' '}
                                {monitor.alsoSearched
                                    .map(
                                        (round) =>
                                            `${fullDayLabel(
                                                round.from
                                            )} t/m ${fullDayLabel(round.to)}`
                                    )
                                    .join(', ')}
                                . Wat hierboven staat, blijft dus staan.
                            </p>
                        )}

                        {actionProps && <ActionBlock actions={actionProps} />}

                        {(props.reprocessResult || props.reprocessError) && (
                            <ReprocessReport
                                result={props.reprocessResult}
                                error={props.reprocessError}
                                onDismiss={props.onDismissReprocess}
                                onReload={monitor.reload}
                            />
                        )}

                        {trace.owner && (
                            <p className="trace-verdict__owner">
                                Hoort bij{' '}
                                <strong>{trace.owner.function}</strong> in{' '}
                                {trace.owner.appDisplayName ?? trace.owner.app}
                                {trace.owner.queue &&
                                    ` op queue ${trace.owner.queue}`}
                                {trace.owner.lastHeartbeat
                                    ? `, die ${formatAge(
                                          trace.owner.lastHeartbeat
                                      )} geleden nog een teken van leven gaf.`
                                    : ', waarvan geen recent teken van leven is.'}
                                <span
                                    className="trace-panel__hint"
                                    tabIndex={0}
                                    role="note"
                                    title={BASIS_HINT[trace.owner.basis]}
                                    aria-label={BASIS_HINT[trace.owner.basis]}
                                >
                                    <FontAwesomeIcon icon="circle-info" />
                                </span>
                                {trace.owner.note && (
                                    <em className="trace-verdict__note">
                                        {' '}
                                        {trace.owner.note}
                                    </em>
                                )}
                            </p>
                        )}
                    </div>

                    {/*
                        One block where there used to be two.

                        A chain of cards with a filtered table under it was the wrong shape.
                        The cards were the same size whether they had anything to say or not,
                        their facts sat behind a click, and clicking one filtered the table
                        below to the rows written at that step - which for most steps is none,
                        so the answer to "what happened here" was an empty card above an empty
                        table. Now the steps run down the page in the order the object passes
                        them, each with its own time and its own log.
                    */}
                    <div className="trace-panel__block">
                        <h4 className="trace-panel__block-title">
                            Tijdlijn van dit object
                            {dayWindow && (
                                <span
                                    className={
                                        guessed
                                            ? 'trace-panel__window trace-panel__window--guessed'
                                            : 'trace-panel__window'
                                    }
                                    title={WINDOW_TITLE[dayWindow.source]}
                                >
                                    {dayLabel(dayWindow.from)} t/m{' '}
                                    {dayLabel(dayWindow.to)}
                                    {WINDOW_SUFFIX[dayWindow.source]}
                                    {/*
                                        The days rather than only the ends of the range,
                                        whenever those are not the same thing. A window built
                                        around the day something was found on skips the weeks in
                                        between, and reading "30 apr t/m 16 jun" as seven weeks
                                        of logs is the wrong conclusion to invite.
                                    */}
                                    {dayCount(dayWindow) >
                                        dayWindow.days.length && (
                                        <span className="trace-panel__window-gap">
                                            {' '}
                                            ({dayWindow.days.length} dagen, niet
                                            aansluitend)
                                        </span>
                                    )}
                                </span>
                            )}
                            <span className="trace-panel__block-hint">
                                Van boven naar beneden de weg die dit object
                                aflegt. Per stap staat erboven wat erin gaat en
                                eronder wat eruit komt - inclusief het
                                queuebericht dat de stap eronder oppakt.
                            </span>
                        </h4>

                        {journey ? (
                            <TraceFlow
                                journey={journey}
                                timeline={trace.timeline}
                                read={monitor.readData}
                                onShowStackTraces={monitor.showStackTraces}
                                stackTracesLoaded={monitor.stackTraces}
                                renderAction={(kind) => {
                                    const action = trace.actions.find(
                                        (candidate) => candidate.kind === kind
                                    );

                                    return action && actionProps ? (
                                        <ActionButton
                                            action={action}
                                            actions={actionProps}
                                            compact
                                        />
                                    ) : null;
                                }}
                            />
                        ) : (
                            <TraceCallout tone="muted">
                                Er is geen route te tekenen voor dit object, dus
                                er valt ook niets per stap te tonen.
                            </TraceCallout>
                        )}

                        {trace.timeline.length === 0 && (
                            <TraceCallout
                                tone="muted"
                                title="Geen regels in deze dagen"
                            >
                                Er is in de gelezen dagen niets over dit object
                                weggeschreven. Dat is niet hetzelfde als: er is
                                niets gebeurd - de meeste stappen loggen bij
                                succes helemaal niets. Lees de kanttekeningen
                                onderaan voordat je hier een conclusie aan
                                hangt.
                            </TraceCallout>
                        )}
                    </div>
                </>
            )}

            {trace && (subject || trace.related.length > 0) && (
                <div className="trace-panel__block">
                    <h4 className="trace-panel__block-title">
                        {subject
                            ? 'Verwante objecten'
                            : 'Objecten die dit id noemen'}
                    </h4>

                    {trace.related.length === 0 ? (
                        <p className="trace-panel__empty">
                            Geen ander object verwijst binnen deze periode naar
                            dit object.
                        </p>
                    ) : (
                        <ul className="trace-related">
                            {trace.related.map((related) => (
                                <RelatedRow
                                    key={`${related.objectType}-${related.relationId}`}
                                    related={related}
                                    onOpen={() =>
                                        monitor.open({
                                            id: related.relationId,
                                            objectType: related.objectType,
                                            relationId: related.relationId,
                                        })
                                    }
                                />
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {trace && (
                <div className="trace-panel__method">
                    <SearchReport
                        search={trace.search}
                        next={lastSearch}
                        rounds={monitor.alsoSearched}
                        loading={monitor.loading}
                        onSearchFurtherBack={monitor.searchFurtherBack}
                    />

                    {trace.inbound && <InboundReport inbound={trace.inbound} />}

                    {trace.gaps.length > 0 && (
                        <details className="trace-gaps">
                            <summary className="trace-gaps__summary">
                                Wat deze trace niet kan zien (
                                {trace.gaps.length})
                            </summary>

                            <ul className="trace-gaps__list">
                                {trace.gaps.map((gap) => (
                                    <li key={gap}>{gap}</li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}
        </CollapsibleSection>
    );
};
